/**
 * Owner: Masa Bayu
 * File: line-painted-face-object.tsx
 * Date:Time: 2026-10-15 08:20
 * Description: R11.4/R11.7e Face L2 · segments + wordStarts · edit commit.
 */

import type { CSSProperties, ReactElement, ReactNode } from 'react';
import {
  isWordStartsCardinalityOk,
  measureLineEdit,
  paintLineFromPdf,
  paintTokens,
  type Font,
  type QXWLine,
} from '@qx/qx-line-paint';
import type { AssetLibrary, TextElement } from '../extract/spsu-types';
import { resolveCssFontFamily } from '../fonts/spsu-fonts';
import {
  defaultFaceLineFontProvider,
  logFaceLineEditFail,
} from '../hosts/face-line-edit-commit';
import { getRenderPipe, shouldUseLinePaintV2 } from '../feature-flags';
import { faceStackZIndex, resolveFaceInk } from './qx-editor-page-face-objects';

export type FaceLineTextChange = (
  elementId: string,
  newContent: string,
  tipLine?: QXWLine,
) => void;

type Props = {
  readonly element: TextElement;
  readonly assets: AssetLibrary;
  readonly page: { readonly width: number; readonly height: number };
  readonly editable?: boolean;
  readonly onTextChange?: FaceLineTextChange;
};

function toQxwLine(element: TextElement, fontFamily: string): QXWLine {
  return {
    id: element.id,
    text: element.content,
    layout: {
      x: element.bbox.x,
      y: element.bbox.y,
      width: element.bbox.width,
      height: element.bbox.height,
    },
    typography: {
      family: fontFamily,
      size: element.style.fontSize,
    },
    ...(element.linePaint !== undefined ? { linePaint: element.linePaint } : {}),
    ...(element.style.fontWeight === 'bold' || element.style.fontWeight === 700
      ? { marks: [{ type: 'bold' }] }
      : {}),
  };
}

function childrenFromPaint(
  text: string,
  paint: ReturnType<typeof paintLineFromPdf>,
): ReactNode {
  if (paint.paintMode === 'segments' && paint.segments !== undefined) {
    return paint.segments.map((seg, i) => (
      <span
        key={`seg-${i}`}
        data-segment-index={i}
        data-segment-x={String(seg.x)}
        data-segment-width={String(seg.width)}
        style={{
          position: 'absolute',
          left: `${seg.x}pt`,
          top: 0,
          width: `${seg.width}pt`,
          display: 'inline-block',
          boxSizing: 'border-box',
          whiteSpace: 'pre',
          ...(seg.wordSpacingPt !== 0
            ? { wordSpacing: `${seg.wordSpacingPt}pt` }
            : {}),
          ...(seg.letterSpacingPt !== 0
            ? { letterSpacing: `${seg.letterSpacingPt}pt` }
            : {}),
        }}
      >
        {seg.text}
      </span>
    ));
  }
  if (paint.paintMode === 'uniform-word-spacing' || paint.wordStarts === undefined) {
    return text;
  }
  const words = paintTokens(text);
  const starts = paint.wordStarts;
  if (
    words.length === 0 ||
    starts.length === 0 ||
    !isWordStartsCardinalityOk(text, starts)
  ) {
    return text;
  }
  return words.map((word, i) => {
    const start = starts[i]!;
    return (
      <span
        key={`${i}-${word}`}
        data-word-index={i}
        style={{
          position: 'absolute',
          left: `${start}pt`,
          top: 0,
          whiteSpace: 'pre',
        }}
      >
        {word}
      </span>
    );
  });
}

/**
 * L2 line paint shell · FAIL-CLOSED caller must check linePaint before using.
 * Editable → plain text contentEditable (R11.5) · segments/word spans on read.
 */
export function LinePaintedFaceObject({
  element,
  assets,
  page,
  editable = false,
  onTextChange,
}: Props): ReactElement {
  const fontFamily = resolveCssFontFamily(element.style.fontFamily, assets);
  const tipLine = toQxwLine(element, fontFamily);
  const font: Font | null = defaultFaceLineFontProvider(fontFamily);
  const paint = paintLineFromPdf({
    line: tipLine,
    page,
    ...(font !== null ? { font } : {}),
  });
  const ink = resolveFaceInk(element.style.color);
  const unit = element.bbox.unit ?? 'pt';
  const useSegmentSpans =
    !editable &&
    paint.paintMode === 'segments' &&
    paint.segments !== undefined;
  const useWordSpans =
    !editable &&
    !useSegmentSpans &&
    paint.paintMode === 'word-starts' &&
    paint.wordStarts !== undefined;

  const style: CSSProperties = {
    position: paint.position,
    left: paint.left,
    top: paint.top,
    width: paint.width,
    height: paint.height,
    fontFamily: paint.fontFamily,
    fontSize: paint.fontSize,
    ...(paint.fontWeight !== undefined ? { fontWeight: paint.fontWeight } : {}),
    ...(paint.fontStyle !== undefined ? { fontStyle: paint.fontStyle } : {}),
    ...(paint.direction !== undefined ? { direction: paint.direction } : {}),
    ...(paint.wordSpacing !== undefined && !editable && !useSegmentSpans
      ? { wordSpacing: paint.wordSpacing }
      : {}),
    whiteSpace: paint.whiteSpace,
    color: ink,
    WebkitTextFillColor: ink,
    zIndex: faceStackZIndex(element),
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    overflow: 'visible',
    outline: 'none',
    cursor: editable ? 'text' : 'default',
  };

  return (
    <div
      className="qx-line-painted-object"
      data-element-id={element.id}
      data-element-type="text"
      data-qxeditor-element={element.id}
      data-qxeditor-face-text=""
      data-render-pipe="v2"
      data-line-id={paint.dataLineId}
      data-paint-mode={editable ? 'edit-plain' : paint.paintMode}
      data-line-paint="true"
      dir={paint.direction}
      style={style}
      contentEditable={editable}
      spellCheck={false}
      suppressContentEditableWarning
      onBlur={(event) => {
        if (!onTextChange) return;
        const next = event.currentTarget.textContent ?? '';
        if (
          getRenderPipe() === 'v2' &&
          shouldUseLinePaintV2(tipLine.linePaint)
        ) {
          // LINE-02 · no redistribute yet · plain tip text (R11.5 hold)
          onTextChange(element.id, next);
          return;
        }
        if (
          getRenderPipe() === 'v2' &&
          tipLine.linePaint?.wordStarts !== undefined
        ) {
          const measured = measureLineEdit(
            tipLine,
            next,
            defaultFaceLineFontProvider,
          );
          if ('error' in measured) {
            event.currentTarget.textContent = element.content;
            logFaceLineEditFail({
              lineId: element.id,
              error: measured.error,
            });
            return;
          }
          onTextChange(element.id, measured.text, measured);
          return;
        }
        onTextChange(element.id, next);
      }}
    >
      {useSegmentSpans || useWordSpans
        ? childrenFromPaint(element.content, paint)
        : element.content}
      <span data-bbox-unit={unit} hidden />
    </div>
  );
}
